import { sortBy } from "lodash";

import { Report, ReportsResponse } from "services/types/reports.type";
import { convertAppointmentResponseToModel } from "utils/appointments.util";
import { formatValidQueryParamUrlCommaSeparated } from "utils/common";
import { formatPatient } from "utils/patients.util";
import { convertReportResponseToModel } from "utils/reports.utils";
import { formatSale } from "utils/sales.util";
import { viteEnv } from "utils/vite-env.util";

import { emptySplitApi } from "./empty-split-api";
import { Appointment } from "./types/appointments.type";
import {
  PatientProduct,
  PatientProductsResponse
} from "./types/patient-products.type";
import { Patient } from "./types/patients.type";
import { Sale, SalesResponse } from "./types/sales.type";

export const PATIENT_DATA_SOURCE_QUERY =
  "{is_active,id,first_name,middle_name,last_name,preferred_name,title,suffix,birthdate,sex,marital_status,primary_contact_notes,secondary_contact_notes,ssn,employer_name,employment_status,phone,email,street_address_1,street_address_2,city,state,country,zip_code,secondary_phone,secondary_phone_type,secondary_email,secondary_street_address_1,secondary_street_address_2,secondary_city,secondary_state,secondary_country,secondary_zip_code,emergency_contact_relationship,emergency_contact_relationship_other,emergency_contact_first_name,emergency_contact_last_name,emergency_contact_street_address_1,emergency_contact_street_address_2,emergency_contact_city,emergency_contact_country,emergency_contact_state,emergency_contact_zip_code,emergency_contact_phone,emergency_contact_email,hipaa_sign_date,preferred_provider,preferred_clinic{id,name,noah_provider,display_name,google_place_id},age_years,photo,next_appointment_moment,next_appointment{id,start_moment,clinic{timezone},status,patient_feedback_rating},previous_appointment_moment,previous_appointment{id,start_moment,clinic{timezone},status,patient_feedback_rating},following_appointment_moment,following_appointment{id,start_moment,clinic{timezone},status,patient_feedback_rating},referral_source{id,name,is_active,referred_type},sub_referral_source{id,name,is_active,object_id,object_type,parent{id}},referring_physician{id,npi,first_name,last_name,title,suffix,phone,email,fax_phone,practice_name,street_address_1,street_address_2,city,state,zip_code},primary_care_physician{id,npi,first_name,last_name,title,suffix,phone,email,fax_phone,practice_name,street_address_1,street_address_2,city,state,zip_code},payment_source_type,balance,phone_type,preferred_communication_method,noah_id,noah_patient_id,patient_responsibility_balance,insurer_responsibility_balance,managed_care_responsibility_balance,primary_insurance_policy{policyholder_phone,policyholder_street_address_1,policyholder_street_address_2,policyholder_city,policyholder_state,policyholder_zip_code,policyholder_employer_name,policyholder_birthdate,policyholder_sex,policyholder_first_name,policyholder_last_name,policyholder_relationship,insurer_phone_number,plan_name,policyholder_group_number,hearing_benefit,deductible,copay,notes,insurer{name},policyholder_member_id},secondary_insurance_policy{policyholder_phone,policyholder_street_address_1,policyholder_street_address_2,policyholder_city,policyholder_state,policyholder_zip_code,policyholder_employer_name,policyholder_birthdate,policyholder_sex,policyholder_first_name,policyholder_last_name,policyholder_relationship,insurer_phone_number,plan_name,policyholder_group_number,hearing_benefit,deductible,copay,notes,insurer{name},policyholder_member_id},box_folder_id,crm_segment,managed_care_plan,hearing_loss_left,hearing_loss_right,left_ear_hearing_aid{id,status,is_in_use,is_active,sale{id},inventory_product{id,condition,loanable,demoable,product{display_name},serial_number,manufacturer_warranty_expiration_date,loss_damage_warranty_expiration_date},description,extended_warranty_expiration_date,manufacturer_warranty_expiration_date,purchase_date,serial_number,ear,due_date},right_ear_hearing_aid{id,status,is_active,is_in_use,sale{id},inventory_product{id,condition,product{display_name},serial_number,manufacturer_warranty_expiration_date,loss_damage_warranty_expiration_date},description,extended_warranty_expiration_date,manufacturer_warranty_expiration_date,purchase_date,serial_number,ear,due_date},hearing_loss_type_left,hearing_loss_type_right,hearing_loss_shape_left,hearing_loss_shape_right,most_recent_report{id},active_products_request{id},assigned_tags{id,tag{id,name,is_active}},latest_icd10_codes}";

const PATIENT_SALE_DATA_SOURCE_QUERY =
  "{id,service_date,provider{id,license_number,signature,npi,first_name,title,suffix,last_name},items{id,cpt_code{id,code},description,exchanged_at,returned_at,price,price_table_item{id,price,max_discount,is_active,price_table{id,name}},discount,discount_type{id,is_active,type,subtype,mode,value,title,product{id,type,display_name,master_product{subtype}}},quantity,patient_responsibility,insurer_responsibility,tax_rate,tax_amount,taxable,total,quickbooks_line_id,insurers{amount,balance,id,insurer{id}},patient_products{id,status,service_plan_expiration_date,inventory_product{id,status,serial_number,manufacturer_warranty_expiration_date,loss_damage_warranty_expiration_date,condition,checked_in_at,ear,specification,is_active,quantity,ordered_quantity,reserved_quantity,loanable,demoable}},product{id,display_name,max_discount,loss_and_deductible_amount,cpt_code{id,code},master_product{id,type,subtype,specification,manufacturer{name},model}},inventory_product{id,status,color,ear,serial_number,condition,specification,loanable,demoable,available_quantity,manufacturer_warranty_expiration_date,loss_damage_warranty_expiration_date},order_item{id,patient_product{id,serial_number,ear},inventory_product{id,serial_number,product{id,display_name}}},order{id,type,clinic_order_number,status},claim_form_data,created_by,updated_by,delivered_at},total,payments{id,amount,method,payer_type,payment_date,check_number,payment_reference,description},total_paid,total_adjusted,adjustments,balance,status,invoices,refunds,notes,patient_responsibility_total,insurer_responsibility_total,patient_responsibility_total_paid,insurer_responsibility_total_paid,patient_responsibility_total_adjusted,insurer_responsibility_total_adjusted,patient_responsibility_total_refunds,insurer_responsibility_total_refunds,patient_responsibility_balance,insurer_responsibility_balance,total_refunds,payment_source_type,managed_care_plan,insurance_policy{id,insurer{id,name}}}";

const PATIENT_PRODUCTS_DATA_SOURCE_QUERY =
  "{id,patient{id},sale{id,service_date,items{product{id},quantity}},sale_item,inventory_product{id,status,condition,loanable,demoable,created_at,product{id,display_name,loss_and_deductible_amount,master_product{manufacturer,specification,type,subtype,description,model,msrp}},specification,serial_number,manufacturer_warranty_expiration_date,loss_damage_warranty_expiration_date,color},order_item{id,manufacturer{name},model,price,quantity,description,total,tracking_number,created_at},status,type,subtype,extended_warranty_expiration_date,description,is_active,is_in_use,serial_number,ear,manufacturer_warranty_expiration_date,loss_damage_warranty_expiration_date,purchase_date,due_date,service_plan_expiration_date,delivered_at,delivered_at_display}";

const PATIENT_REPORT_DATA_SOURCE_QUERY =
  "{id,appointment{id,start_moment,staff_member{first_name,last_name,suffix,title,signature},clinic{id,name,logo,timezone},type{id,name,default_duration}},report_date,summary,is_signed,signed_at,diagnosis{id,hearing_loss_left,hearing_loss_right,hearing_loss_type_left,hearing_loss_type_right,hearing_loss_shape_left,hearing_loss_shape_right},word_recognition_report,speech_in_noise_report,tympanometry_report}";

export const mergeTagsApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getMergeTagsPatient: builder.query<
      Patient,
      { patientId: number; query?: string }
    >({
      query: ({ patientId, query = PATIENT_DATA_SOURCE_QUERY }) =>
        `${viteEnv.VITE_SUNO_API}/patients/${patientId}/?query=${query}`,
      providesTags: (result) => [
        { type: "MergeTagsPatient" as const, id: result?.id }
      ],
      transformResponse: formatPatient
    }),
    getMergeTagsReport: builder.query<Report, number>({
      query: (reportId) =>
        `${
          viteEnv.VITE_SUNO_API
        }/reports/${reportId}/?query=${PATIENT_REPORT_DATA_SOURCE_QUERY}`,
      providesTags: (result) => [
        { type: "MergeTagsReport" as const, id: result?.id }
      ],
      transformResponse: convertReportResponseToModel
    }),
    getMergeTagsCurrentReport: builder.query<Report | null, number>({
      query: (patientId) =>
        `${
          viteEnv.VITE_SUNO_API
        }/reports/?${formatValidQueryParamUrlCommaSeparated({
          patient: patientId,
          ordering: "-id",
          limit: 1,
          offset: 0
        })}&query=${PATIENT_REPORT_DATA_SOURCE_QUERY}`,
      providesTags: (result) => [
        { type: "MergeTagsReport" as const, id: result?.id }
      ],
      transformResponse: (response: ReportsResponse) =>
        response[0] ? convertReportResponseToModel(response[0]) : null
    }),
    getMergeTagsSale: builder.query<
      Sale | null,
      { appointmentId: number; patientId: number }
    >({
      query: ({ appointmentId, patientId }) => {
        return `${
          viteEnv.VITE_SUNO_API
        }/sales/?${formatValidQueryParamUrlCommaSeparated({
          appointment: appointmentId,
          patient: patientId
        })}&limit=1&offset=0&ordering=-pk&query=${PATIENT_SALE_DATA_SOURCE_QUERY}`;
      },
      providesTags: (result) => [
        {
          type: "MergeTagsSale" as const,
          id: result?.id
        }
      ],
      transformResponse: (response: SalesResponse) =>
        response.results[0] ? formatSale(response.results[0]) : null
    }),
    getMergeTagsSaleById: builder.query<Sale, { id: number }>({
      query: ({ id }) => {
        return `${
          viteEnv.VITE_SUNO_API
        }/sales/${id}/?query=${PATIENT_SALE_DATA_SOURCE_QUERY}`;
      },
      providesTags: (result) => [
        {
          type: "MergeTagsSale" as const,
          id: result?.id
        }
      ],
      transformResponse: (sale: Sale) => formatSale(sale)
    }),
    getMergeTagsAppointment: builder.query<Appointment, number>({
      query: (appointmentId) =>
        `${
          viteEnv.VITE_SUNO_API
        }/appointments/${appointmentId}/?query={id,patient_feedback_rating,outcome,outcome_reason,outcome_notes,start_moment,end_moment,status,companion_name,companion_type,notes,physician_referral,referral_source{id,name},sub_referral_source{id,name},referring_physician,staff_member{id,first_name,last_name,suffix,title,is_active,signature,email},room{name,clinic{id,name,logo,practice{id,name,logo}}},clinic{name,timezone,practice{id,name,logo}},type{id,default_duration,name,is_opportunity,general_type},chart_note}`,
      providesTags: (result) => [
        { type: "MergeTagsAppointment" as const, id: result?.id }
      ],
      transformResponse: convertAppointmentResponseToModel
    }),
    getMergeTagsPatientProducts: builder.query<
      PatientProduct[],
      { patientId: number }
    >({
      query: ({ patientId }) =>
        `${
          viteEnv.VITE_SUNO_API
        }/patient-products/?${formatValidQueryParamUrlCommaSeparated({
          is_active: true,
          limit: 100,
          offset: 0,
          patient: patientId
        })}&query=${PATIENT_PRODUCTS_DATA_SOURCE_QUERY}`,
      transformResponse: (response: PatientProductsResponse) => {
        return sortBy(response.results, [
          (product) => {
            if (product.ear === "L") return 1;
            if (product.ear === "R") return 2;
            return 2;
          }
        ]);
      },
      providesTags: (result) =>
        result?.map((product) => ({
          type: "MergeTagsPatientProduct" as const,
          id: product.id
        })) ?? []
    })
  })
});

export const {
  useGetMergeTagsPatientQuery,
  useGetMergeTagsReportQuery,
  useGetMergeTagsCurrentReportQuery,
  useGetMergeTagsSaleQuery,
  useLazyGetMergeTagsSaleByIdQuery,
  useGetMergeTagsSaleByIdQuery,
  useGetMergeTagsAppointmentQuery,
  useGetMergeTagsPatientProductsQuery
} = mergeTagsApi;
